import { Directive } from "@angular/core";
import { FrontendPaginatedDto } from "../interfaces/frontendPaginatedDto";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { debounceTime, pairwise } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalService } from "../service/modal.service";
import _ from "lodash";
import { getDateTime } from "../funktionen/globale-funktionen";
import { EntityTyp } from "../enums/entity-typ.enum";

@Directive()
export abstract class BaseListDirective<T> {
  page: FrontendPaginatedDto<T>;
  filter: FormGroup;

  modalType?: EntityTyp;
  inital = true;

  protected constructor(
    protected fb: FormBuilder,
    protected route: ActivatedRoute,
    protected router: Router,
    protected modalService: ModalService
  ) {}

  get queryParams() {
    return this.route.snapshot.queryParams;
  }

  getOpenModalParams(id: number) {
    return {
      ...this.queryParams,
      modalTyp: this.modalType,
      modalEntityId: id,
    };
  }

  async initFilter(filterFields) {
    this.filter = this.fb.group({
      sortCol: [null],
      sortDir: [null],
      limit: [10],
      page: [1],
      ...filterFields,
      deleted: [false],
    });
    if (Object.keys(this.queryParams).length) {
      const limit = this.filter.get("limit").value;
      const page = this.filter.get("page").value;
      this.filter.reset();
      this.filter.patchValue({
        limit,
        page,
        ...this.queryParams,
      });
    }
    await this.setQuerystring();
    this.filter.valueChanges.pipe(debounceTime(200)).subscribe((newVal) => {
      this.filter.patchValue(
        { ...newVal, page: 1 },
        {
          emitEvent: false,
        }
      );
      this.setQuerystring();
    });
    this.route.queryParams.pipe(pairwise()).subscribe(([oldParams, newParams]) => {
      const oldFilterParams = this.getFilterParams(oldParams);
      const newFilterParams = this.getFilterParams(newParams);
      if (!_.isEqual(oldFilterParams, newFilterParams)) {
        this.setFilter();
      }
      if (!this.modalService.hasOpenModals) {
        this.openModalFromList();
      }
    });
    this.route.params.pipe(pairwise()).subscribe((params) => {
      const limit = this.filter.get("limit").value;
      this.filter.reset();
      this.filter.patchValue({
        limit,
        page: 1,
        ...this.queryParams,
      });
      this.setFilter();
    });
    this.setFilter();
    this.openModalFromList();
  }

  setPage(pageInfo: any) {
    if (this.inital) {
      this.inital = false;
      return;
    }
    this.filter.get("page").setValue(pageInfo.offset + 1, { emitEvent: false });
    this.setQuerystring();
  }

  onSort(sortEvent: any) {
    this.filter.patchValue({
      sortCol: sortEvent?.column?.prop,
      sortDir: sortEvent.newValue.toUpperCase(),
    });
  }

  setFilter() {
    const rawQueryParams = this.queryParams;
    const rawParams = this.getParsedParams();
    const { modalTyp = null, modalEntityId = null, ...params } = { ...rawParams, ...rawQueryParams };
    for (const key in params) {
      if (Array.isArray(params[key])) {
        params[key] = params[key].map((entry) => this.convertParam(entry));
      } else {
        params[key] = this.convertParam(params[key]);
      }
      if (!Array.isArray(params[key]) && key === "status") {
        if (params[key]) {
          params[key] = [params[key]];
        }
      }
    }
    this.filter.patchValue(params, { emitEvent: false });
    this.reload();
  }

  syncPageNumber() {
    const currentPage = this.page.currentPage;
    this.page.currentPage = 1;
    setTimeout(() => (this.page.currentPage = currentPage), 0);
  }

  registerDateRangeComponent(fromName: string, toName: string) {
    const dateRangeFc = new FormControl({
      from: this.filter.get(fromName).value,
      to: this.filter.get(toName).value,
    });

    dateRangeFc.valueChanges.subscribe((newVal) => {
      this.filter.patchValue({
        [fromName]: newVal.from,
        [toName]: newVal.to,
      });
    });
    return dateRangeFc;
  }

  protected abstract reload(): void;

  protected getParsedParams() {
    return null;
  }

  private async openModalFromList() {
    if (this.queryParams?.["modalTyp"] && this.queryParams["modalEntityId"]) {
      await this.modalService.openEntityModal(this.queryParams["modalTyp"], this.queryParams["modalEntityId"]);
      this.reload();
    }
  }

  private getFilterParams(allParams) {
    const { modalTyp = null, modalEntityId = null, ...filterParams } = allParams;
    return filterParams;
  }

  private async setQuerystring() {
    const filter = this.filter.value;
    for (const key in filter) {
      if (filter[key] instanceof Date) {
        filter[key] = getDateTime(filter[key]).toISO();
      }
      if (filter[key] === undefined || filter[key] === null || filter[key] === "") {
        delete filter[key];
      }
    }
    let newParams = filter;
    if (this.queryParams["modalTyp"] && this.queryParams["modalEntityId"]) {
      newParams = {
        ...filter,
        modalTyp: this.queryParams["modalTyp"],
        modalEntityId: this.queryParams["modalEntityId"],
      };
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: newParams,
    });
  }

  private convertParam(param: string) {
    if (param === "") {
      return null;
    } else if (param === "true") {
      return true;
    } else if (param === "false") {
      return false;
    } else if (!isNaN(+param) && (param === "0" || !param.startsWith("0"))) {
      return +param;
    }
    return param;
  }
}
